import { Timestamp } from 'firebase/firestore';
import type { EventCategoryColor } from './lib/eventCategoryColors';
import type { CategoryActionSettings, CategoryActionValidPeriods, EventAction } from './lib/eventActions';
import { isPlausibleEmail } from './utils/contactLinks';
import { normalizeQuestions } from './lib/eventQuestions';

export type Language = 'en' | 'si';

// Per-event custom questions (QUESTIONS-01). The server model
// (functions/src/modules/events/eventQuestions.ts) is the authority for
// validation; these types are the shared contract every client-side
// consumer — the booking form, the admin question builder, the answers
// display — codes against verbatim. Do not rename or restructure without
// updating the server model to match.
export type EventQuestionType = 'text' | 'textarea' | 'select' | 'radio' | 'checkbox';

export interface LocalizedText {
  en: string;
  si: string;
}

export interface EventQuestionOption {
  value: string;
  label: LocalizedText;
}

export interface EventQuestion {
  id: string;
  label: LocalizedText;
  type: EventQuestionType;
  required?: boolean;
  helpText?: LocalizedText;
  options?: EventQuestionOption[];
}

// A booking's stored answer snapshots the question's label and type at
// submission time (not just its id), so an admin editing or deleting a
// question later does not turn a historical booking into an unreadable
// orphan key. Mirrors StoredAnswer in the server model.
export interface QuestionAnswer {
  questionId: string;
  label: LocalizedText;
  type: EventQuestionType;
  value: string | string[];
}

/** Admin-managed event category (global, not year-scoped). */
export interface EventCategory {
  id: string;
  name: { en: string; si: string };
  slug?: string;       // URL-friendly name used by ?category=
  color?: EventCategoryColor; // chip colour; falls back to 'stone'
  order?: number;     // optional manual sort
  // Bilingual rich-HTML blocks shown at the top of the category's public tab.
  instructions?: { en: string; si: string }; // how-to / what-to-bring guidance
  notes?: { en: string; si: string };         // supplementary remarks
  // Short bilingual summary + schedule shown on the Home browse-by-category cards.
  description?: { en: string; si: string };
  schedule?: { en: string; si: string };      // e.g. "Wednesdays", "Sep 23 – Nov 2"
}

// Shared allocation/robe-rotation contract (VAS-01).
// A slot's effective status is derived from booking presence when `status`
// is absent, so existing pooja events stay backward-compatible.
export type EventType = 'pooja' | 'robe-rotation';
export type SlotStatus = 'available' | 'allocated' | 'waitlisted';

export interface EventData {
  id: string;
  name: {
    en: string;
    si: string;
  };
  description: {
    en: string;
    si: string;
  };
  summary?: LocalizedText;
  venue?: LocalizedText;
  startTime: string;
  endTime: string;
  type: 'weekday' | 'weekend' | 'daily';
  booking?: BookingData | null; // Raw booking data from Firestore
  bookings?: BookingData[]; // Confirmed bookings for capacity-limited events
  waitlist?: BookingData[]; // Array of users on the waitlist
  date?: string; // Optional since it might be added later
  endDate?: string;
  recurrence?: {
    rule: 'daily' | 'weekly' | 'monthly';
    endDate?: string;
  };
  seriesId?: string;
  year?: number;
  imageUrl?: string;
  categoryId?: string;
  bookingLimit?: number;
  bookingLimitLabel?: { en: string; si: string };
  bookingEmailTemplate?: string;
  bookingCodeRequired?: boolean;
  bookingCode?: string | null;
  bookingCodeHash?: string;
  specialNote?: string;
  confirmedCount?: number;
  waitlistCount?: number;
  availability?: 'available' | 'waitlisted';
  action?: EventAction;
  // VAS-01 allocation/robe-rotation fields (all optional for back-compat)
  eventType?: EventType; // undefined => treated as 'pooja'
  status?: SlotStatus; // undefined => derived from booking presence
  collectionTime?: string; // robe pickup time, default '17:00'
  overnight?: boolean; // robe kept overnight at devotee residence
  // QUESTIONS-01: admin-authored questions rendered on the booking form for
  // this event. Absent/empty means no custom questions.
  questions?: EventQuestion[];
  // FH-01: admin-controlled spotlight on the homepage's "Featured Events"
  // section. Absent/false means not featured (opt-in, not opt-out).
  featuredOnHome?: boolean;
  waitlistEnabled?: boolean;
}

export interface EventSlot {
  id: string;
  name: {
    en: string;
    si: string;
  };
  description: {
    en: string;
    si: string;
  };
  summary?: LocalizedText;
  venue?: LocalizedText;
  startTime: string;
  endTime: string;
  type: 'weekday' | 'weekend' | 'daily';
  booking: Booking | null;
  bookings?: Booking[];
  waitlist: Booking[];
  date: string; // The date of the event slot
  endDate?: string;
  seriesId?: string;
  year?: number;
  imageUrl?: string;
  categoryId?: string;
  bookingLimit?: number;
  bookingLimitLabel?: { en: string; si: string };
  bookingEmailTemplate?: string;
  bookingCodeRequired?: boolean;
  bookingCode?: string | null;
  bookingCodeHash?: string;
  specialNote?: string;
  confirmedCount?: number;
  waitlistCount?: number;
  availability?: 'available' | 'waitlisted';
  action?: EventAction;
  // VAS-01 allocation/robe-rotation fields (optional on the interface so
  // DTO-style literals built via `Omit<EventSlot, ...>` stay assignable;
  // EventSlotImpl always populates them at runtime).
  eventType?: EventType;
  status?: SlotStatus;
  collectionTime?: string;
  overnight?: boolean;
  questions?: EventQuestion[];
  featuredOnHome?: boolean;
  waitlistEnabled?: boolean;
  // Methods for easy access to language-specific fields
  getName(lang: string): string;
  getDescription(lang: string): string;
  // Effective allocation status: explicit `status` wins, otherwise derived
  // from booking/waitlist presence for back-compat.
  getStatus?(): SlotStatus;
  isRobeRotation?(): boolean;
}

export class EventSlotImpl implements EventSlot {
  id: string;
  name: {
    en: string;
    si: string;
  };
  description: {
    en: string;
    si: string;
  };
  summary?: LocalizedText;
  venue?: LocalizedText;
  startTime: string;
  endTime: string;
  type: 'weekday' | 'weekend' | 'daily';
  booking: Booking | null;
  bookings: Booking[];
  waitlist: Booking[];
  date: string; // The date of the event slot
  endDate?: string;
  seriesId?: string;
  year?: number;
  imageUrl?: string;
  categoryId?: string;
  bookingLimit?: number;
  bookingLimitLabel?: { en: string; si: string };
  bookingEmailTemplate?: string;
  bookingCodeRequired: boolean;
  bookingCode?: string | null;
  bookingCodeHash?: string;
  specialNote?: string;
  confirmedCount?: number;
  waitlistCount?: number;
  availability?: 'available' | 'waitlisted';
  action: EventAction;
  eventType: EventType;
  status?: SlotStatus;
  collectionTime?: string;
  overnight?: boolean;
  questions: EventQuestion[];
  featuredOnHome: boolean;
  waitlistEnabled?: boolean;

  constructor(data: EventData, date: string) {
    this.id = data.id;
    this.name = data.name;
    this.description = data.description;
    this.summary = data.summary;
    this.venue = data.venue;
    this.startTime = data.startTime;
    this.endTime = data.endTime;
    this.type = data.type;
    const confirmedBookings = Array.isArray(data.bookings) && data.bookings.length > 0
      ? data.bookings
      : data.booking
        ? [data.booking]
        : [];
    this.bookings = confirmedBookings.map(b => new BookingImpl(b));
    // Keep the first booking mirrored for older consumers and Firestore data.
    this.booking = this.bookings[0] ?? null;
    this.waitlist = data.waitlist ? data.waitlist.map(b => new BookingImpl(b)) : [];
    this.date = date;
    this.endDate = data.endDate;
    this.seriesId = data.seriesId;
    this.year = data.year;
    this.imageUrl = data.imageUrl;
    this.categoryId = data.categoryId;
    this.bookingLimit = data.bookingLimit;
    this.bookingLimitLabel = data.bookingLimitLabel;
    this.bookingEmailTemplate = data.bookingEmailTemplate;
    this.bookingCodeRequired = data.bookingCodeRequired === true;
    this.bookingCode = typeof data.bookingCode === 'string' ? data.bookingCode : undefined;
    this.bookingCodeHash = typeof data.bookingCodeHash === 'string' ? data.bookingCodeHash : undefined;
    this.specialNote = typeof data.specialNote === 'string' ? data.specialNote : undefined;
    this.confirmedCount = data.confirmedCount;
    this.waitlistCount = data.waitlistCount;
    this.availability = data.availability;
    this.action = data.action ?? 'join';
    this.eventType = data.eventType ?? 'pooja';
    this.status = data.status;
    // Robe rotation defaults: collected at 5 PM, kept overnight.
    this.collectionTime = data.collectionTime ?? (this.eventType === 'robe-rotation' ? '17:00' : undefined);
    this.overnight = data.overnight ?? (this.eventType === 'robe-rotation' ? true : undefined);
    // Re-normalize even though the server already sanitizes on write: a
    // malformed/stale `questions` array must not crash the dynamic question
    // renderer in BookingModal.
    this.questions = normalizeQuestions(data.questions);
    this.featuredOnHome = data.featuredOnHome === true;
    this.waitlistEnabled = data.waitlistEnabled;
  }

  getName(lang: string): string {
    return lang === 'si' ? this.name.si : this.name.en;
  }

  getDescription(lang: string): string {
    return lang === 'si' ? this.description.si : this.description.en;
  }

  // `availability` is computed server-side in EventModule.listPublicEvents from
  // live enrollment counts against the booking limit. It answers exactly one
  // question — is this event FULL? — and it is the only field here that
  // reflects the real count, so it is the authority on fullness.
  //
  // It used to be consulted only in the negative: 'waitlisted' returned early,
  // but 'available' fell through to the derivation below, where a stored
  // `status` could contradict it. Nothing in the enrollment path maintains
  // `status` (the enrollment module writes confirmedCount/waitlistCount and
  // never touches it), so a stale 'waitlisted' left over from an earlier data
  // model silently won. That happened in production on "2 Days Meditation
  // Retreat": 1 confirmed against a limit of 30, the server correctly sent
  // availability 'available', and the page still offered "join waitlist" —
  // five devotees queued for an event with 29 free places.
  //
  // The derivation is unchanged; it is only clamped, so nothing may claim the
  // event is full when the server says it is not. 'allocated' is not a
  // fullness claim and passes through untouched.
  getStatus(): SlotStatus {
    if (this.availability === 'waitlisted') {
      if (this.waitlistEnabled === false) return 'allocated';
      return 'waitlisted';
    }

    const derived = this.deriveStatus();
    if (this.availability === 'available' && derived === 'waitlisted') return 'available';
    return derived;
  }

  // Back-compat derivation for projections that carry no `availability` —
  // notably the admin list, which ships real bookings instead.
  private deriveStatus(): SlotStatus {
    if (this.bookingLimit) {
      if (this.bookings.length >= this.bookingLimit) {
        return this.waitlistEnabled === false ? 'allocated' : 'waitlisted';
      }
      if (this.bookings.length > 0) return 'available';
    }
    if (this.status) return this.status;
    if (this.booking) return 'allocated';
    if (this.waitlist.length > 0) {
      return this.waitlistEnabled === false ? 'allocated' : 'waitlisted';
    }
    return 'available';
  }

  isRobeRotation(): boolean {
    return this.eventType === 'robe-rotation';
  }
}

export interface Booking {
  id?: string;
  bookingReference?: string;
  devoteeName: string;
  email: string;
  phone: string;
  address: string;
  /** Map-only locality correction; address keeps the exact submitted value. */
  correctedAddress?: string;
  addressCorrection?: { originalAddress: string; correctedAddress: string; confidence: number };
  notes: string;
  timestamp: Date; // Converted timestamp for use in the app
  date: string;
  eventId?: string;
  status?: 'confirmed' | 'waitlisted' | 'cancelled';
  createdAt?: Date;
  cancelledAt?: Date;
  // Set when an admin allocates a slot offline without notifying the devotee;
  // the onEventUpdate trigger skips the confirmation email when true.
  suppressNotification?: boolean;
  // QUESTIONS-01: the devotee's answers to the event's custom questions,
  // in stored (snapshotted) form. Absent/empty means the event had no
  // questions, or the booking predates this feature.
  answers?: QuestionAnswer[];
}

export interface BookingData {
  id?: string;
  bookingReference?: string;
  devoteeName: string;
  email: string;
  phone: string;
  address: string;
  /** Map-only locality correction; address keeps the exact submitted value. */
  correctedAddress?: string;
  addressCorrection?: { originalAddress: string; correctedAddress: string; confidence: number };
  notes: string;
  timestamp: Timestamp; // Raw Firestore timestamp
  date: string;
  eventId?: string;
  status?: 'confirmed' | 'waitlisted' | 'cancelled';
  createdAt?: Timestamp | Date | string;
  cancelledAt?: Timestamp | Date | string;
  reminderSent?: boolean;
  suppressNotification?: boolean;
  answers?: QuestionAnswer[];
}

export class BookingImpl implements Booking {
  id?: string;
  bookingReference?: string;
  devoteeName: string;
  email: string;
  phone: string;
  address: string;
  /** Map-only locality correction; address keeps the exact submitted value. */
  correctedAddress?: string;
  addressCorrection?: { originalAddress: string; correctedAddress: string; confidence: number };
  notes: string;
  timestamp: Date;
  date: string;
  eventId?: string;
  status?: 'confirmed' | 'waitlisted' | 'cancelled';
  createdAt?: Date;
  cancelledAt?: Date;
  answers?: QuestionAnswer[];

  constructor(data: BookingData | Booking) { // Accept both raw Firestore data and processed data
    this.id = data.id;
    this.bookingReference = data.bookingReference;
    this.devoteeName = data.devoteeName;
    this.email = data.email;
    this.phone = data.phone;
    this.address = data.address;
    this.correctedAddress = data.correctedAddress;
    this.addressCorrection = data.addressCorrection;
    this.notes = data.notes;
    this.date = data.date;
    this.eventId = data.eventId;
    this.status = data.status;
    this.answers = data.answers;
    this.createdAt = BookingImpl.toDate(data.createdAt);
    this.cancelledAt = BookingImpl.toDate(data.cancelledAt);
    
    // HTTP Event API responses serialize Firestore timestamps to ISO strings,
    // while direct Firestore reads provide Timestamp objects. Check the runtime
    // shape before using the `in` operator so wire-format bookings do not make
    // the admin roster fall back to the public projection.
    this.timestamp = BookingImpl.toDate(data.timestamp) ?? new Date();
  }

  private static toDate(value: unknown): Date | undefined {
    if (
      value
      && typeof value === 'object'
      && 'toDate' in value
      && typeof value.toDate === 'function'
    ) {
      return value.toDate();
    }
    if (value instanceof Date) return value;
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? undefined : parsed;
    }
    return undefined;
  }
}

export interface DonationItem {
  id: string;
  name: {
    en: string;
    si: string;
  };
  description: {
    en: string;
    si: string;
  };
  instructions: {
    en: string;
    si: string;
  };
  categories: Array<{
    en: string;
    si: string;
  }>;
  imageUrl?: string;
  // Optional admin-supplied figures. `cost` is the suggested cost per item;
  // `quantityAvailable` is how many are still needed/available for donation.
  cost?: number;
  quantityAvailable?: number;
  // Bilingual noun paired with `quantityAvailable` in the UI (e.g. "Pirikara",
  // "Sets"), so counts read in the temple's own wording instead of a hardcoded
  // generic. Mirrors `bookingLimitLabel` on events. Absent = unlabelled count.
  quantityAvailableLabel?: { en: string; si: string };
  // Server-maintained aggregate — sum of quantity across pending + approved
  // requests for this item. Written by the onDonationRequestAllocationWritten
  // Firestore trigger so the public donations page can render "N remaining"
  // without needing read access to samadhi_donation_requests.
  quantityAllocated?: number;
  // Server-owned aggregate generation. Clients must never write this field.
  allocationVersion?: number;
  // Legacy items without this field auto-approve by default.
  autoApprove?: boolean;
  requestEmailTemplate?: string;
  // Bank deposit reference fields surfaced in confirmation emails
  // via {particulars} and {code} placeholders.
  particulars?: string;
  code?: string;
  action?: EventAction;
  year?: number;
  order?: number;
  // FH-01: admin-controlled spotlight on the homepage's "Featured Donations"
  // section. Absent/false means not featured (opt-in, not opt-out).
  featuredOnHome?: boolean;
}

export class FetchedDonationItem implements DonationItem {
  id: string;
  name: {
    en: string;
    si: string;
  };
  description: {
    en: string;
    si: string;
  };
  instructions: {
    en: string;
    si: string;
  };
  categories: Array<{
    en: string;
    si: string;
  }>;
  imageUrl?: string;
  cost?: number;
  quantityAvailable?: number;
  quantityAvailableLabel?: { en: string; si: string };
  quantityAllocated?: number;
  allocationVersion?: number;
  autoApprove: boolean;
  requestEmailTemplate?: string;
  particulars?: string;
  code?: string;
  action?: EventAction;
  year?: number;
  order?: number;
  featuredOnHome: boolean;

  constructor(data: DonationItem) {
    this.id = data.id;
    this.name = data.name;
    this.description = data.description;
    this.instructions = data.instructions;
    this.categories = data.categories;
    this.imageUrl = data.imageUrl;
    this.cost = data.cost;
    this.quantityAvailable = data.quantityAvailable;
    this.quantityAvailableLabel = data.quantityAvailableLabel;
    this.quantityAllocated = data.quantityAllocated;
    this.allocationVersion = data.allocationVersion;
    this.autoApprove = data.autoApprove !== false;
    this.requestEmailTemplate = data.requestEmailTemplate;
    this.particulars = data.particulars;
    this.code = data.code;
    this.action = data.action;
    this.year = data.year;
    this.order = data.order;
    this.featuredOnHome = data.featuredOnHome === true;
  }
}

export const ADMIN_ROLES = ['admin', 'finance', 'trustMember', 'coordinator', 'supporter'] as const;

export type AdminRole = typeof ADMIN_ROLES[number];

export interface Admin {
  id: string;
  username: string;
  email: string;
  role: AdminRole;
  // Editable via the admin profile page. Absent on legacy docs; the
  // notifyAdmins() backend filter treats undefined as "subscribed to all".
  displayName?: string;
  notificationSubscriptions?: string[];
  backupEmail?: string;   // Self-service only. Used for the 2FA-reset security notification.
  backupPhone?: string;   // Self-service only. Display/manual-contact only — never used for automated delivery.
}

export interface Contact {
  id: string;
  name: string;
  role: string;
  phoneNumbers: string[];
  email: string;
  // Free-form HTML describing when to contact this person (rendered
  // sanitized via DOMPurify at every read site). Optional; older records
  // predate the field.
  description?: string;
  year?: number;
}

export interface Activity {
  name: string;
  date: string;
  description: string;
}

export interface DonationCategory {
  id: string;
  name_en: string;
  name_si: string;
  description_en: string;
  description_si: string;
}

export interface DonationRequest {
  id?: string;
  donationItemId: string;
  donationItemName: {
    en: string;
    si: string;
  };
  devoteeName: string;
  email: string;
  phone: string;
  // Optional in practice, not just in principle: requests written before the
  // public endpoint persisted these fields have none of them, and the admin panel
  // crashed reading .length off an absent selectedCategories.
  address?: string;
  quantity: number;
  message?: string;
  notes?: string;            // what the endpoint used to call `message`
  selectedCategories?: string[];
  status: 'pending' | 'approved' | 'fulfilled' | 'rejected';
  submittedAt: Timestamp;
  updatedAt?: Timestamp;
  paymentProvider?: 'stripe' | 'paypal' | 'manual';
  paymentId?: string;
  amount?: number;
  requestEmailTemplate?: string;
  version?: number;
  allocationVersion?: number;
}

export interface SiteSettings {
  id?: string;
  defaultLanguage: Language;
  fromEmailAddress?: string;
  replyToEmailAddress?: string;
  fromEmailName?: {
    en: string;
    si: string;
  };
  replyToEmailName?: {
    en: string;
    si: string;
  };
  siteName: {
    en: string;
    si: string;
  };
  siteDescription: {
    en: string;
    si: string;
  };
  reminderDays?: number;
  eventReminderEmailTemplate?: string;
  coordinatorReminderEmailTemplate?: string;
  robeCollectionReminderEmailTemplate?: string;
  katinaYear?: number;
  katinaStartDate?: string;
  katinaEndDate?: string;
  // When false, the Stripe (credit card) payment option is hidden on the public
  // donation form. Absent is treated as enabled, preserving existing behaviour.
  stripeEnabled?: boolean;
  // When enabled, public routes show the maintenance screen while admin routes
  // remain available for administrators to sign in and manage the site.
  maintenanceMode?: boolean;
  // Admin-controlled public visibility by event category and donation
  // category/type. Absent values mean every category is enabled, preserving
  // existing behaviour for settings documents created before this feature.
  enabledEventCategoryIds?: string[];
  enabledDonationCategoryKeys?: string[];
  disabledEventActionCategoryIds?: string[];
  disabledDonationActionCategoryKeys?: string[];
  eventActionValidPeriodsByCategory?: CategoryActionValidPeriods;
  donationActionValidPeriodsByCategory?: CategoryActionValidPeriods;
  // Legacy action settings retained for backwards-compatible reads only. The
  // availability setting above is intentionally independent from action type.
  enabledEventActionsByCategory?: CategoryActionSettings;
  enabledDonationActionsByCategory?: CategoryActionSettings;
  // Legacy global lists retained so older settings documents can be migrated
  // without changing their meaning on first load.
  enabledEventActions?: EventAction[];
  enabledDonationActions?: EventAction[];
  updatedAt?: Timestamp;
}

export interface EmailDeliverySettings {
  defaultLanguage: Language;
  fromEmailAddress: string;
  fromEmailName: {
    en: string;
    si: string;
  };
  replyToEmailAddress: string;
  replyToEmailName: {
    en: string;
    si: string;
  };
  updatedAt?: Timestamp;
}

export interface EmailTemplateAttachment {
  name: string;
  contentType: string;
  size: number;
  storagePath: string;
  downloadUrl: string;
  uploadedAt?: Timestamp;
}

export interface EmailTemplate {
  id?: string;
  name: string;
  subject: string;
  body: string;
  attachment?: EmailTemplateAttachment | null;
  updatedAt?: Timestamp;
  updatedBy?: string;
}

// A historical snapshot of an EmailTemplate, stored under the template's
// `versions` subcollection. Structurally a template record; `id` is the
// version document id and `templateId` (when present) references the parent.
export interface EmailTemplateVersion extends EmailTemplate {
  templateId?: string;
}

// Monk Accommodation Management Types

export interface VehicleGroup {
  id: string;
  name: string; // e.g., "A-2", "A-3", "D-3", "D-4"
  type: 'arrival' | 'departure' | 'both';
  capacity: number;
  description?: string;
  year?: number;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface AccommodationLocation {
  id: string;
  name: string;
  address: string;
  capacity: number;
  contactPerson?: string;
  contactPhone?: string;
  notes?: string;
  year?: number;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface AccommodationAssignment {
  locationId: string;
  locationName: string;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  notes?: string;
}

export interface MonkVisitData {
  id?: string;
  // Monk Information
  location: string; // City/Region (e.g., "Hamilton", "ACK N/Shore")
  therosName: string; // Venerable name
  phone?: string;
  
  // Invitation Details
  invited: boolean;
  invitedBy?: string; // Name of the person who invited
  passportCopyReceived: boolean;
  
  // Arrival Details
  preferredArrivalDate?: string; // ISO date string
  preferredArrivalTime?: string; // e.g., "Late Afternoon", "Afternoon"
  confirmedArrivalDateTime?: string; // ISO datetime string
  arrivalFlightNumber?: string;
  airportPickupTime?: string;
  arrivalVehicleGroupId?: string;
  arrivalVehicleGroupName?: string;
  
  // Departure Details
  preferredDepartureDate?: string; // ISO date string
  preferredDepartureTime?: string; // e.g., "Afternoon"
  confirmedDepartureDateTime?: string; // ISO datetime string
  departureFlightNumber?: string;
  templePickupTime?: string; // Pickup time from temple to airport
  departureVehicleGroupId?: string;
  departureVehicleGroupName?: string;
  
  // Accommodation Details
  accommodationAssignments: AccommodationAssignment[];
  
  // Coordinator Details
  assignedPersonName?: string;
  assignedPersonContact?: string;
  
  // Ticket Management
  ticketsBooked: boolean;
  ticketDeliveryMethod?: 'email' | 'whatsapp' | 'not_sent';
  
  // Status and Metadata
  status: 'invited' | 'confirmed' | 'arrived' | 'departed' | 'cancelled';
  notes?: string;
  year?: number;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
  createdBy?: string;
  updatedBy?: string;
}

export interface MonkVisit extends Omit<MonkVisitData, 'createdAt' | 'updatedAt'> {
  id: string;
  createdAt?: Date;
  updatedAt?: Date;
  isCurrentlyStaying(currentDate?: Date): boolean;
  isArrivingSoon(daysAhead?: number, currentDate?: Date): boolean;
  isDepartingSoon(daysAhead?: number, currentDate?: Date): boolean;
}

const normalizeMonkVisitDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;

  if (typeof value === 'object' && value !== null && 'toDate' in value) {
    const toDate = (value as {toDate?: unknown}).toDate;
    if (typeof toDate === 'function') return (toDate as () => Date)();
  }

  const parsed = new Date(value as string | number);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

export class MonkVisitImpl implements MonkVisit {
  id: string;
  location: string;
  therosName: string;
  phone?: string;
  invited: boolean;
  invitedBy?: string;
  passportCopyReceived: boolean;
  preferredArrivalDate?: string;
  preferredArrivalTime?: string;
  confirmedArrivalDateTime?: string;
  arrivalFlightNumber?: string;
  airportPickupTime?: string;
  arrivalVehicleGroupId?: string;
  arrivalVehicleGroupName?: string;
  preferredDepartureDate?: string;
  preferredDepartureTime?: string;
  confirmedDepartureDateTime?: string;
  departureFlightNumber?: string;
  templePickupTime?: string;
  departureVehicleGroupId?: string;
  departureVehicleGroupName?: string;
  accommodationAssignments: AccommodationAssignment[];
  assignedPersonName?: string;
  assignedPersonContact?: string;
  ticketsBooked: boolean;
  ticketDeliveryMethod?: 'email' | 'whatsapp' | 'not_sent';
  status: 'invited' | 'confirmed' | 'arrived' | 'departed' | 'cancelled';
  notes?: string;
  year?: number;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  updatedBy?: string;

  constructor(data: MonkVisitData) {
    this.id = data.id || '';
    this.location = data.location;
    this.therosName = data.therosName;
    this.phone = data.phone;
    this.invited = data.invited;
    this.invitedBy = data.invitedBy;
    this.passportCopyReceived = data.passportCopyReceived;
    this.preferredArrivalDate = data.preferredArrivalDate;
    this.preferredArrivalTime = data.preferredArrivalTime;
    this.confirmedArrivalDateTime = data.confirmedArrivalDateTime;
    this.arrivalFlightNumber = data.arrivalFlightNumber;
    this.airportPickupTime = data.airportPickupTime;
    this.arrivalVehicleGroupId = data.arrivalVehicleGroupId;
    this.arrivalVehicleGroupName = data.arrivalVehicleGroupName;
    this.preferredDepartureDate = data.preferredDepartureDate;
    this.preferredDepartureTime = data.preferredDepartureTime;
    this.confirmedDepartureDateTime = data.confirmedDepartureDateTime;
    this.departureFlightNumber = data.departureFlightNumber;
    this.templePickupTime = data.templePickupTime;
    this.departureVehicleGroupId = data.departureVehicleGroupId;
    this.departureVehicleGroupName = data.departureVehicleGroupName;
    this.accommodationAssignments = data.accommodationAssignments || [];
    this.assignedPersonName = data.assignedPersonName;
    this.assignedPersonContact = data.assignedPersonContact;
    this.ticketsBooked = data.ticketsBooked;
    this.ticketDeliveryMethod = data.ticketDeliveryMethod;
    this.status = data.status;
    this.notes = data.notes;
    this.year = data.year;
    this.createdBy = data.createdBy;
    this.updatedBy = data.updatedBy;

    // Firestore snapshots provide Timestamp objects; the HTTP admin API returns
    // their ISO-string JSON representation. Normalize both at this boundary.
    this.createdAt = normalizeMonkVisitDate(data.createdAt);
    this.updatedAt = normalizeMonkVisitDate(data.updatedAt);
  }

  // Helper method to check if monk is currently staying
  isCurrentlyStaying(currentDate: Date = new Date()): boolean {
    const arrivalDate = this.confirmedArrivalDateTime ? new Date(this.confirmedArrivalDateTime) : null;
    const departureDate = this.confirmedDepartureDateTime ? new Date(this.confirmedDepartureDateTime) : null;
    
    if (!arrivalDate || !departureDate) return false;
    
    return currentDate >= arrivalDate && currentDate <= departureDate;
  }

  // Helper method to check if monk is arriving soon (within days)
  isArrivingSoon(daysAhead: number = 7, currentDate: Date = new Date()): boolean {
    if (!this.confirmedArrivalDateTime) return false;
    
    const arrivalDate = new Date(this.confirmedArrivalDateTime);
    const futureDate = new Date(currentDate);
    futureDate.setDate(futureDate.getDate() + daysAhead);
    
    return arrivalDate >= currentDate && arrivalDate <= futureDate;
  }

  // Helper method to check if monk is departing soon (within days)
  isDepartingSoon(daysAhead: number = 7, currentDate: Date = new Date()): boolean {
    if (!this.confirmedDepartureDateTime) return false;
    
    const departureDate = new Date(this.confirmedDepartureDateTime);
    const futureDate = new Date(currentDate);
    futureDate.setDate(futureDate.getDate() + daysAhead);
    
    return departureDate >= currentDate && departureDate <= futureDate;
  }
}

// Maintenance Task Tracker (VAS-01) — mirrors
// "Main Event & Maintenance Tasks.xlsx". Year-scoped via YearContext.
export type MaintenanceTaskStatus =
  | 'pending'
  | 'in-progress'
  | 'completed'
  | 'booked'
  | 'na';

// A single Vas team member assigned to a maintenance task, with contact details.
// Distinct from VasTeamMember (the Vas Teams roster type below): here contact
// fields are always present (normalized to '' on read).
export interface MaintenanceTeamMember {
  name: string;
  email: string;
  phone: string;
}

export interface MaintenanceTaskData {
  id?: string;
  mainGroup: string; // top-level grouping (e.g., "Kitchen", "Grounds")
  subGroup?: string; // optional sub-grouping
  task: string; // task title/description
  mainCoordinator?: string;
  /**
   * @deprecated Legacy single bilingual assignee. Retained only so existing
   * documents can still be read; migrated to `vasTeamMembers` on read
   * (see MaintenanceTaskImpl). New writes use `vasTeamMembers`.
   */
  vasTeamMember?: {
    en?: string;
    si?: string;
  };
  // Vas team members assigned to the task (repeatable; name + contact details).
  vasTeamMembers?: MaintenanceTeamMember[];
  // Optional link to a VasTeam (see VasTeamData) responsible for the task.
  // Stored as the team's Firestore document id; resolved to a name at render time.
  vasTeamId?: string;
  dueDate?: string; // ISO date string
  status: MaintenanceTaskStatus;
  details?: string;
  year?: number;
  seriesId?: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface MaintenanceTask
  extends Omit<MaintenanceTaskData, 'createdAt' | 'updatedAt' | 'vasTeamMember' | 'vasTeamMembers'> {
  id: string;
  createdAt?: Date;
  updatedAt?: Date;
  // Always an array — legacy single-assignee data is migrated on read.
  vasTeamMembers: MaintenanceTeamMember[];
}

export class MaintenanceTaskImpl implements MaintenanceTask {
  id: string;
  mainGroup: string;
  subGroup?: string;
  task: string;
  mainCoordinator?: string;
  vasTeamMembers: MaintenanceTeamMember[];
  vasTeamId?: string;
  dueDate?: string;
  status: MaintenanceTaskStatus;
  details?: string;
  year?: number;
  seriesId?: string;
  createdAt?: Date;
  updatedAt?: Date;

  constructor(data: MaintenanceTaskData) {
    this.id = data.id || '';
    this.mainGroup = data.mainGroup;
    this.subGroup = data.subGroup;
    this.task = data.task;
    this.mainCoordinator = data.mainCoordinator;
    this.vasTeamMembers = MaintenanceTaskImpl.normalizeVasTeamMembers(data);
    this.vasTeamId = data.vasTeamId;
    this.dueDate = data.dueDate;
    this.status = data.status;
    this.details = data.details;
    this.year = data.year;
    this.seriesId = data.seriesId;

    if (data.createdAt && 'toDate' in data.createdAt && typeof data.createdAt.toDate === 'function') {
      this.createdAt = data.createdAt.toDate();
    } else if (data.createdAt instanceof Date) {
      this.createdAt = data.createdAt;
    } else if (data.createdAt) {
      this.createdAt = new Date(data.createdAt as unknown as string | number);
    }

    if (data.updatedAt && 'toDate' in data.updatedAt && typeof data.updatedAt.toDate === 'function') {
      this.updatedAt = data.updatedAt.toDate();
    } else if (data.updatedAt instanceof Date) {
      this.updatedAt = data.updatedAt;
    } else if (data.updatedAt) {
      this.updatedAt = new Date(data.updatedAt as unknown as string | number);
    }
  }

  // Prefer the new array; otherwise migrate the legacy bilingual assignee into a
  // single name-only member so existing documents keep displaying their assignee.
  private static normalizeVasTeamMembers(data: MaintenanceTaskData): MaintenanceTeamMember[] {
    if (Array.isArray(data.vasTeamMembers)) {
      return data.vasTeamMembers.map(member => ({
        name: member?.name ?? '',
        email: member?.email ?? '',
        phone: member?.phone ?? '',
      }));
    }
    const legacyName = data.vasTeamMember?.en || data.vasTeamMember?.si;
    return legacyName ? [{ name: legacyName, email: '', phone: '' }] : [];
  }
}

// Firestore Timestamp (or already-converted Date) → Date, for Impl constructors.
const toDateOrUndefined = (value?: Timestamp | Date): Date | undefined => {
  if (value instanceof Date) return value;
  return value && typeof value.toDate === 'function' ? value.toDate() : undefined;
};

// ----- VAS Teams (from Vas Pinkama YYYY workbook, Main Teams sheet) -----
// Each team represents an area of responsibility (e.g., "VAS Aradhanawa Pirikara").
// Members are named people with optional phone/email; the workbook only carries names,
// so contact fields default to empty and are entered by admins.

export interface VasTeamMember {
  name: string;      // display name (matches Excel first-name convention)
  role?: string;     // free-form (e.g., "coordinator", "member")
  phone?: string;    // E.164 preferred (e.g., "+64211234567") — used by wa.me
  email?: string;    // used by mailto: links
  notes?: string;
}

export interface VasTeamData {
  id?: string;
  name: string;               // area / task title
  trustMember?: string;       // responsible Trust member(s)
  coordinator?: string;       // team coordinator (optional; often the same as trustMember)
  members: VasTeamMember[];   // roster
  whatsappGroupLink?: string; // https://chat.whatsapp.com/... invite link
  notes?: string;
  year: number;
  order?: number;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface VasTeam extends Omit<VasTeamData, 'createdAt' | 'updatedAt'> {
  id: string;
  createdAt?: Date;
  updatedAt?: Date;
  memberCount(): number;
  contactableMembers(): VasTeamMember[]; // members with phone OR email
  memberEmails(): string[];              // deduped, valid-looking emails
}

export class VasTeamImpl implements VasTeam {
  id: string;
  name: string;
  trustMember?: string;
  coordinator?: string;
  members: VasTeamMember[];
  whatsappGroupLink?: string;
  notes?: string;
  year: number;
  order?: number;
  createdAt?: Date;
  updatedAt?: Date;

  constructor(data: VasTeamData) {
    this.id = data.id || '';
    this.name = data.name;
    this.trustMember = data.trustMember;
    this.coordinator = data.coordinator;
    this.members = Array.isArray(data.members) ? data.members : [];
    this.whatsappGroupLink = data.whatsappGroupLink;
    this.notes = data.notes;
    this.year = data.year;
    this.order = data.order;
    this.createdAt = toDateOrUndefined(data.createdAt);
    this.updatedAt = toDateOrUndefined(data.updatedAt);
  }

  memberCount(): number {
    return this.members.length;
  }

  contactableMembers(): VasTeamMember[] {
    return this.members.filter(m => (m.phone && m.phone.trim()) || (m.email && m.email.trim()));
  }

  memberEmails(): string[] {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const m of this.members) {
      const raw = (m.email || '').trim();
      if (!isPlausibleEmail(raw)) continue;
      const key = raw.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(raw);
    }
    return out;
  }
}

// ----- VAS Budget (year-scoped, category-total rows) -----
// Represents ONE line of the current-year budget (e.g., "Airfare Contributions",
// "Maintenance", "Cash Pirikara for Main Pinkama"). Historical (2025) actuals live
// in the Excel workbook — we model the 2026 budget as editable category totals.

export type VasBudgetKind = 'inflow' | 'outflow';

/**
 * A share of a budget line carried by one Vas team. Amounts are deliberately
 * free-form: they are not required to add up to the line's amount, so a line can
 * be partially allocated, or over-allocated while the split is still being argued
 * about. Nothing derives the line total from these.
 */
export interface VasBudgetTeamAllocation {
  teamId: string;
  teamName?: string;  // snapshot of the name at save time, so a deleted team still reads sensibly
  amount: number;     // NZD
}

export interface VasBudgetLineData {
  id?: string;
  year: number;                // e.g., 2026
  kind: VasBudgetKind;         // inflow | outflow
  particulars: string;         // e.g., "Airfare Contributions"
  amount: number;              // NZD
  currency?: string;           // default "NZD"
  category?: string;           // optional grouping (e.g., "Event Costs", "Pirikara")
  subCategories?: string[];    // free-text tags, e.g., ["Robes", "Flights"]
  teamAllocations?: VasBudgetTeamAllocation[];
  notes?: string;
  order?: number;              // display sort
  actualAmount?: number;       // optional running actual (nice-to-have; blank by default)
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

export interface VasBudgetLine extends Omit<VasBudgetLineData, 'createdAt' | 'updatedAt'> {
  id: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export class VasBudgetLineImpl implements VasBudgetLine {
  id: string;
  year: number;
  kind: VasBudgetKind;
  particulars: string;
  amount: number;
  currency?: string;
  category?: string;
  subCategories: string[];
  teamAllocations: VasBudgetTeamAllocation[];
  notes?: string;
  order?: number;
  actualAmount?: number;
  createdAt?: Date;
  updatedAt?: Date;

  constructor(data: VasBudgetLineData) {
    this.id = data.id || '';
    this.year = data.year;
    this.kind = data.kind;
    this.particulars = data.particulars;
    this.amount = typeof data.amount === 'number' ? data.amount : Number(data.amount) || 0;
    this.currency = data.currency || 'NZD';
    this.category = data.category;
    // Default to [] so every reader can map/length without a null check; lines
    // written before these fields existed simply have none.
    this.subCategories = Array.isArray(data.subCategories) ? data.subCategories : [];
    this.teamAllocations = Array.isArray(data.teamAllocations) ? data.teamAllocations : [];
    this.notes = data.notes;
    this.order = data.order;
    this.actualAmount = typeof data.actualAmount === 'number' ? data.actualAmount : undefined;
    this.createdAt = toDateOrUndefined(data.createdAt);
    this.updatedAt = toDateOrUndefined(data.updatedAt);
  }
}

/** Total carried by a line's team allocations. Informational only — see VasBudgetTeamAllocation. */
export function sumTeamAllocations(allocations: VasBudgetTeamAllocation[] = []): number {
  return allocations.reduce((acc, a) => acc + (Number.isFinite(a.amount) ? a.amount : 0), 0);
}

// Pure helpers used by the admin BudgetManager & tests.
export function sumBudgetLines(lines: VasBudgetLine[], kind?: VasBudgetKind): number {
  const filtered = kind ? lines.filter(l => l.kind === kind) : lines;
  return filtered.reduce((acc, l) => acc + (Number.isFinite(l.amount) ? l.amount : 0), 0);
}

export function budgetSurplus(lines: VasBudgetLine[]): number {
  return sumBudgetLines(lines, 'inflow') - sumBudgetLines(lines, 'outflow');
}
